/**
 * Sends POST /api/account/heartbeat/ every 60s when user was active in the last 60s.
 * On 401 response, broadcasts signout to all tabs.
 */

import { apiClient } from "@shared/api/client";
import { broadcast } from "./broadcastChannel";

const HEARTBEAT_INTERVAL_MS = 60_000;

let intervalId: ReturnType<typeof setInterval> | null = null;
let lastActivityTs = 0;

export function recordActivity(): void {
  lastActivityTs = Date.now();
}

async function sendHeartbeat(): Promise<void> {
  const wasActiveRecently = Date.now() - lastActivityTs < HEARTBEAT_INTERVAL_MS;
  if (!wasActiveRecently) return;

  try {
    const res = await apiClient.post<{
      seconds_until_idle_timeout: number;
      seconds_until_absolute_timeout: number;
    }>("/api/account/heartbeat/");

    broadcast({
      type: "heartbeat-ack",
      secondsUntilIdle: res.data.seconds_until_idle_timeout,
      secondsUntilAbsolute: res.data.seconds_until_absolute_timeout,
    });
  } catch (err: unknown) {
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status === 401 || status === 403) {
      broadcast({ type: "signout", reason: "expired" });
    }
  }
}

export function startHeartbeat(): () => void {
  lastActivityTs = Date.now(); // treat mount as activity
  intervalId = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);
  return stopHeartbeat;
}

export function stopHeartbeat(): void {
  if (intervalId !== null) {
    clearInterval(intervalId);
    intervalId = null;
  }
}
